import {
  AppAndAddonActivationStatus,
  ListAppsAndAddonsActivationsStatusesForBusinessResponseAppsAndAddonsActivationStatusesInterface as ActivationStatusesInterface,
} from '@vendasta/accounts/legacy';
import { ResellerItem } from '@vendasta/store';
import { getResellerItemUniqueId, resellerArrayToMap } from '../../core/reseller';
import { StatusMessage, UniqueIdToItemIdAndQuantityMap } from './interface';

export function getStatusMessage(
  status: ActivationStatusesInterface,
  name: string,
  allowMultipleActivations: boolean,
): StatusMessage {
  const t = allowMultipleActivations ? 'info' : 'warn';
  const activationActionString = allowMultipleActivations
    ? 'More will be activated as a part of this order'
    : 'It will not be activated as part of this order';

  switch (status.status) {
    case AppAndAddonActivationStatus.ACTIVATED:
      return {
        text: `This account already has ${
          status.count > 1 ? status.count + ' ' + name + "'s" : name
        }. ${activationActionString}.`,
        type: t,
      };
    case AppAndAddonActivationStatus.PENDING:
      return {
        text: `This account already has ${
          status.count > 1 ? status.count + ' ' + name + "'s" : name
        } pending. ${activationActionString}.`,
        type: t,
      };
  }
  return undefined;
}

// Filter out reseller items that are not multi activatable if they are in the pending / activated state.
export function filterResellerItemsByStatuses(
  resellerItems: ResellerItem[],
  statuses: ActivationStatusesInterface[],
): ResellerItem[] {
  if (!resellerItems) {
    return [];
  }

  if (!statuses) {
    return resellerItems;
  }

  const appStatusMap: Map<string, ActivationStatusesInterface[]> = (resellerItems || []).reduce((m, curr) => {
    const id = getResellerItemUniqueId(curr);
    const riStatuses = statuses.filter((status) => status.appId === id);
    m.set(id, riStatuses);
    return m;
  }, new Map<string, ActivationStatusesInterface[]>());

  return resellerItems.filter((ri) => {
    if (ri.allowMultipleActivations) {
      return true;
    }

    const id = getResellerItemUniqueId(ri);
    if (!appStatusMap.get(id)) {
      return true;
    }

    const riStatuses = appStatusMap.get(id);
    return !riStatuses.some(
      (s) => s.status === AppAndAddonActivationStatus.ACTIVATED || s.status === AppAndAddonActivationStatus.PENDING,
    );
  });
}

// Sort statusMessages by their type
export function sortStatusMessages(statusMessages: StatusMessage[]): StatusMessage[] {
  const warningMessages = (statusMessages || []).filter((m) => m.type === 'warn');
  const infoMessages = (statusMessages || []).filter((m) => m.type === 'info');
  return [...warningMessages, ...infoMessages];
}

export function filterStatusMessagesInOrder(
  addons: ResellerItem[],
  apps: ResellerItem[],
  customProducts: ResellerItem[],
  statuses: ActivationStatusesInterface[],
  resellerItemMap: UniqueIdToItemIdAndQuantityMap,
): StatusMessage[] {
  // Get Map of reseller items that are a part of this order
  const allResellerItems = [...apps, ...addons, ...customProducts];
  const filteredResellerItems = allResellerItems.filter((ri) => {
    return resellerItemMap.has(getResellerItemUniqueId(ri));
  });
  const riMap = resellerArrayToMap(filteredResellerItems);

  // Filter the statuses to only apps that are in this order
  const filteredStatuses = statuses.filter((status) => {
    return status && riMap.has(status.appId);
  });

  return filteredStatuses
    .map((status) => {
      const ri = riMap.get(status.appId);
      return getStatusMessage(status, ri ? ri.name : '', ri ? ri.allowMultipleActivations : false);
    })
    .filter(Boolean);
}
